@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_driver

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.example.project.dashboard.PopupEngineCard
import org.example.project.engineslot.SLOT_BEREICH_WA
import org.example.project.engineslot.SLOT_BEREICH_WE
import org.example.project.engineslot.SLOT_EXTRA_DRIVER_ID
import org.example.project.engineslot.SlotStammdatenApi
import org.example.project.engineslot.slotLadenTouren
import org.example.project.engineslot.slotLiefernBelege
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.service.LoginService
import org.example.project.service.YARD_PALETTENTAUSCH_WA_AKTIV
import org.example.project.service.YARD_PALETTENTAUSCH_WE_AKTIV
import org.example.project.service.YardApi
import org.example.project.service.YardLiveSync
import org.example.project.service.YardSettingsService
import org.example.project.service.ZustandManager
import org.example.project.service.withYardSettingsDefaults
import org.example.project.service.yardSettingsBool
import org.example.project.screens_cus_mobile.mobileYardUpsertOrRemove
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.example.project.yard.WA_SAMMEL_ID_EXTRA
import org.example.project.yard.WA_SAMMEL_PIN_EXTRA
import org.example.project.yard.yardPinCode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

object DriverFlowState {
    val selectedScreenKey = mutableStateOf("DASHBOARD")
    val mobileBackHandler = mutableStateOf<(() -> Boolean)?>(null)
    val selectedMeineAuftraegeDateKey = mutableStateOf("")
    val selectedMeineAuftraegeArtCode = mutableStateOf("")
    val selectedWeAuftragId = mutableStateOf("")
    val selectedYardLieferscheinnummer = mutableStateOf("")
    val selectedWeProviderFirmenId = mutableStateOf("")
    val selectedAuftragArt = mutableStateOf(SLOT_BEREICH_WE)
    val selectedYardAuftrag = mutableStateOf<All?>(null)
    val backgroundScope = MainScope()
}

data class DriverBookingCardModel(
    val booking: All,
    val providerFirmenId: String,
    val artCode: String,
    val adresseLabel: String,
    val gebuchtVonLabel: String,
    val art: String,
    val primaryRef: String,
    val palettenLabel: String,
    val lkwLabel: String = "",
    val aufliegerLabel: String = "",
    val firmaLabel: String = "",
    val zustandLabel: String = "",
    val standortRows: List<All> = emptyList(),
    val routeLabel: String = "",
    val currentTargetLabel: String = "",
    val remainingStopsLabel: String = "",
    val palettentauschAktiv: Boolean = true,
    val sammelPin: String = "",
    val sammelAuftraege: List<All> = emptyList()
)

fun DriverBookingCardModel.isSammelabholung(): Boolean =
    artCode in setOf(SLOT_BEREICH_WE, SLOT_BEREICH_WA) && sammelAuftraege.size > 1

fun DriverBookingCardModel.isDriverKundenhofAuftrag(): Boolean =
    booking.zustand >= ZustandManager.RESERVIERT &&
        booking.zustand < ZustandManager.ABGESCHLOSSEN &&
        booking.zustand != ZustandManager.STORNIERT

fun DriverBookingCardModel.isDriverFinalAuftrag(): Boolean =
    booking.zustand >= ZustandManager.ABGESCHLOSSEN || booking.zustand == ZustandManager.STORNIERT

private fun driverAuftragZustandLabel(zustand: Int): String =
    when {
        zustand == ZustandManager.STORNIERT -> LocalizationManager.get("driver_status_cancelled")
        zustand >= ZustandManager.ABGESCHLOSSEN -> LocalizationManager.get("driver_completed")
        zustand >= ZustandManager.RESERVIERT -> LocalizationManager.get("driver_status_customer_yard")
        else -> LocalizationManager.get("driver_status_created")
    }

internal suspend fun loadDriverBookingCards(
    currentUserId: String,
    currentUserName: String,
    mode: DriverOrderLoadMode = DriverOrderLoadMode.OPEN
): List<DriverBookingCardModel> {
    if (currentUserId.isBlank()) return emptyList()

    val driverOrders = DriverAuftraegeApi.load(mode)
    val cards = mutableListOf<DriverBookingCardModel>()

    driverOrders
        .groupBy { it.sourceFirmenId.trim().ifBlank { it.firmenId.trim() } }
        .forEach { (providerFirmenId, providerOrders) ->
        if (providerFirmenId.isBlank()) return@forEach

        val standorte = runCatching { SlotStammdatenApi.loadStandortQuellen(providerFirmenId) }
            .getOrDefault(emptyList())
        val standorteById = standorte.associateBy { it.id.trim() }
        val yardSettings = runCatching { YardApi.loadYardSettings(providerFirmenId)?.withYardSettingsDefaults() }
            .getOrNull()
            ?: YardSettingsService.defaultSettings()
        val palettentauschWeAktiv = yardSettings.yardSettingsBool(YARD_PALETTENTAUSCH_WE_AKTIV, true)
        val palettentauschWaAktiv = yardSettings.yardSettingsBool(YARD_PALETTENTAUSCH_WA_AKTIV, true)
        val weLkw = providerOrders
            .filter { it.source == "we-lkw" }
            .sortedWith(
                compareBy<All> { it.datum.ifBlank { it.verladedatum } }
                    .thenBy { it.uhrzeitVon.ifBlank { it.verladeuhrzeit }.ifBlank { it.uhrzeitAnmeldung } }
                    .thenBy { it.slotIndex.toIntOrNull() ?: Int.MAX_VALUE }
                    .thenBy { it.lieferscheinnummer }
                    .thenBy { it.id }
            )

        weLkw
            .groupBy { it.driverWeSammelKey() }
            .values
            .forEach { group ->
                val sortedGroup = group.sortedWith(
                    compareBy<All> { it.datum.ifBlank { it.verladedatum } }
                        .thenBy { it.uhrzeitVon.ifBlank { it.verladeuhrzeit }.ifBlank { it.uhrzeitAnmeldung } }
                        .thenBy { it.lieferscheinnummer }
                        .thenBy { it.id }
                )
                val representative = sortedGroup.firstOrNull { it.zustand < ZustandManager.ABGESCHLOSSEN }
                    ?: sortedGroup.first()
                cards += representative.toDriverWeCardModel(providerFirmenId, standorteById, sortedGroup, palettentauschWeAktiv)
            }

        val waLkw = providerOrders
            .filter { it.source == "wa-lkw" }
            .sortedWith(
                compareBy<All> { it.datum.ifBlank { it.verladedatum } }
                    .thenBy { it.uhrzeitVon.ifBlank { it.verladeuhrzeit }.ifBlank { it.uhrzeitAnmeldung } }
                    .thenBy { it.slotIndex.toIntOrNull() ?: Int.MAX_VALUE }
                    .thenBy { it.id }
            )

        waLkw
            .groupBy { it.driverWaSammelKey() }
            .values
            .forEach { group ->
                val sortedGroup = group.sortedWith(
                    compareBy<All> { it.datum.ifBlank { it.verladedatum } }
                        .thenBy { it.uhrzeitVon.ifBlank { it.verladeuhrzeit }.ifBlank { it.uhrzeitAnmeldung } }
                        .thenBy { it.lieferscheinnummer }
                        .thenBy { it.id }
                )
                val representative = sortedGroup.firstOrNull { it.zustand < ZustandManager.ABGESCHLOSSEN }
                    ?: sortedGroup.first()
                cards += representative.toDriverWaCardModel(providerFirmenId, standorteById, sortedGroup, palettentauschWaAktiv)
            }
    }

    return cards.sortedWith(
        compareBy<DriverBookingCardModel> { it.booking.datum.ifBlank { it.booking.verladedatum } }
            .thenBy { it.booking.uhrzeitVon.ifBlank { it.booking.verladeuhrzeit }.ifBlank { it.booking.uhrzeitAnmeldung } }
            .thenBy { it.currentTargetLabel }
            .thenBy { it.booking.ablaufPrio }
    )
}

@Composable
fun DRIVERMeineAuftraege() {
    val currentUser = LoginService.getUser()
    val currentUserId = currentUser?.id.orEmpty().trim()
    val currentUserName = listOf(currentUser?.vorname.orEmpty(), currentUser?.nachname.orEmpty())
        .filter { it.isNotBlank() }
        .joinToString(" ")
        .trim()

    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var bookings by remember { mutableStateOf<List<DriverBookingCardModel>>(emptyList()) }
    var pendingPreparation by remember { mutableStateOf<DriverBookingCardModel?>(null) }
    var blockedEndToEndDelivery by remember { mutableStateOf<Pair<DriverBookingCardModel, DriverBookingCardModel?>?>(null) }
    val scope = rememberCoroutineScope()
    val yardLiveVersion = YardLiveSync.version(includeSnap = true)

    fun openYardLobby(card: DriverBookingCardModel) {
        DriverFlowState.selectedWeAuftragId.value = card.booking.id
        DriverFlowState.selectedYardLieferscheinnummer.value = card.booking.lieferscheinnummer
        DriverFlowState.selectedWeProviderFirmenId.value = card.providerFirmenId
        DriverFlowState.selectedAuftragArt.value = card.artCode
        DriverFlowState.selectedYardAuftrag.value = card.booking
        DriverFlowState.selectedScreenKey.value = "DriverYardLobbyScreen"
    }

    fun endToEndDeliveryBeleg(card: DriverBookingCardModel): All? =
        if (card.artCode != SLOT_BEREICH_WE) {
            null
        } else {
            slotLiefernBelege(card.booking).firstOrNull { it.endToEndFlow && it.flowTyp == "wa_to_we" }
        }

    fun sourceWaForEndToEndDelivery(card: DriverBookingCardModel): DriverBookingCardModel? {
        val source = endToEndDeliveryBeleg(card) ?: card.booking
        if (card.artCode != SLOT_BEREICH_WE || !source.endToEndFlow || source.flowTyp != "wa_to_we") return null
        val sourceLs = source.flowSourceWaTour.ifBlank { source.lieferscheinnummer }.trim()
        return bookings.firstOrNull { candidate ->
            candidate.artCode == SLOT_BEREICH_WA &&
                source.flowId.isNotBlank() &&
                candidate.booking.flowId == source.flowId
        } ?: bookings.firstOrNull { candidate ->
            candidate.artCode == SLOT_BEREICH_WA &&
                candidate.providerFirmenId == source.flowSourceFirmenId &&
                sourceLs.isNotBlank() &&
                candidate.booking.lieferscheinnummer == sourceLs
        } ?: bookings.firstOrNull { candidate ->
            candidate.artCode == SLOT_BEREICH_WA &&
                sourceLs.isNotBlank() &&
                candidate.booking.lieferscheinnummer == sourceLs &&
                candidate.booking.lkwkennzeichen.trim().equals(source.lkwkennzeichen.trim(), ignoreCase = true) &&
                candidate.booking.aufliegerkennzeichen.trim().equals(source.aufliegerkennzeichen.trim(), ignoreCase = true) &&
                candidate.booking.fahrername.trim().equals(source.fahrername.trim(), ignoreCase = true)
        }
    }

    fun sourceWaCompleted(source: DriverBookingCardModel?, target: DriverBookingCardModel): Boolean =
        source?.booking?.zustand?.let { it >= ZustandManager.ABGESCHLOSSEN } == true ||
            target.booking.flowSourceCompleted

    fun requestOpenBooking(card: DriverBookingCardModel) {
        val source = sourceWaForEndToEndDelivery(card)
        val e2eDelivery = endToEndDeliveryBeleg(card)
        val isWaToWeDelivery = card.booking.endToEndFlow && card.booking.flowTyp == "wa_to_we" || e2eDelivery != null
        if (card.artCode == SLOT_BEREICH_WE && isWaToWeDelivery && !sourceWaCompleted(source, card)) {
            blockedEndToEndDelivery = card to source
            return
        }
        when {
            card.isDriverKundenhofAuftrag() -> openYardLobby(card)
            card.isDriverFinalAuftrag() -> Unit
            else -> pendingPreparation = card
        }
    }

    LaunchedEffect(currentUserId, yardLiveVersion) {
        if (currentUserId.isBlank()) {
            loading = false
            bookings = emptyList()
            return@LaunchedEffect
        }

        loading = true
        error = null

        runCatching {
            loadDriverBookingCards(currentUserId, currentUserName)
        }.onSuccess {
            bookings = it
        }.onFailure {
            bookings = emptyList()
            error = it.message ?: LocalizationManager.get("driver_orders_load_failed")
        }

        loading = false
    }

    val selectedPreparation = pendingPreparation
    if (selectedPreparation == null) {
        DriverMeineAuftraegeLayout(
            loading = loading,
            error = error,
            bookings = bookings,
            onOpenBooking = ::requestOpenBooking
        )
    } else {
        val card = selectedPreparation
        DriverPreparationPopup(
            card = card,
            onSave = { preparation ->
                val vorbereitet = card.booking.withDriverPreparation(preparation)
                pendingPreparation = null
                openYardLobby(card.copy(booking = vorbereitet))
            },
            onCancel = { pendingPreparation = null }
        )
    }

    blockedEndToEndDelivery?.let { (_, source) ->
        DriverEndToEndBlockedPopup(
            sourceAvailable = source != null,
            onClose = { blockedEndToEndDelivery = null },
            onOpenSource = {
                blockedEndToEndDelivery = null
                if (source != null) requestOpenBooking(source)
            }
        )
    }
}

private suspend fun updateDriverBookingCardsForLkw(
    cards: List<DriverBookingCardModel>,
    incoming: All,
    artCode: String,
    currentUserId: String,
    currentUserName: String
): List<DriverBookingCardModel> {
    val providerFirmenId = incoming.firmenId.trim()
    if (providerFirmenId.isBlank()) return cards
    val incomingKey = if (artCode == SLOT_BEREICH_WA) incoming.driverWaSammelKey() else incoming.driverWeSammelKey()
    val matchesDriver = bookingMatchesDriver(incoming, currentUserId, currentUserName)
    val standorte = runCatching { SlotStammdatenApi.loadStandortQuellen(providerFirmenId) }.getOrDefault(emptyList())
    val standorteById = standorte.associateBy { it.id.trim() }
    val yardSettings = runCatching { YardApi.loadYardSettings(providerFirmenId)?.withYardSettingsDefaults() }
        .getOrNull()
        ?: YardSettingsService.defaultSettings()
    val palettentauschAktiv = if (artCode == SLOT_BEREICH_WA) {
        yardSettings.yardSettingsBool(YARD_PALETTENTAUSCH_WA_AKTIV, true)
    } else {
        yardSettings.yardSettingsBool(YARD_PALETTENTAUSCH_WE_AKTIV, true)
    }

    var touched = false
    val next = cards.mapNotNull { card ->
        if (card.artCode != artCode || card.providerFirmenId != providerFirmenId) return@mapNotNull card
        val cardKey = if (artCode == SLOT_BEREICH_WA) card.booking.driverWaSammelKey() else card.booking.driverWeSammelKey()
        if (cardKey != incomingKey) return@mapNotNull card
        touched = true
        if (!matchesDriver) return@mapNotNull null
        val groupSource = card.sammelAuftraege.ifEmpty { card.standortRows }.ifEmpty { listOf(card.booking) }
        val updatedGroup = mobileYardUpsertOrRemove(groupSource, incoming, incoming.id.ifBlank { incoming.lfdNr.toString() }) { true }
        val representative = updatedGroup.firstOrNull { it.zustand < ZustandManager.ABGESCHLOSSEN } ?: updatedGroup.firstOrNull()
            ?: return@mapNotNull null
        if (artCode == SLOT_BEREICH_WA) {
            representative.toDriverWaCardModel(providerFirmenId, standorteById, updatedGroup, palettentauschAktiv)
        } else {
            representative.toDriverWeCardModel(providerFirmenId, standorteById, updatedGroup, palettentauschAktiv)
        }
    }.toMutableList()

    if (!touched && matchesDriver) {
        next += if (artCode == SLOT_BEREICH_WA) {
            incoming.toDriverWaCardModel(providerFirmenId, standorteById, listOf(incoming), palettentauschAktiv)
        } else {
            incoming.toDriverWeCardModel(providerFirmenId, standorteById, listOf(incoming), palettentauschAktiv)
        }
    }

    return next.sortedWith(
        compareBy<DriverBookingCardModel> { it.booking.datum.ifBlank { it.booking.verladedatum } }
            .thenBy { it.booking.uhrzeitVon.ifBlank { it.booking.verladeuhrzeit }.ifBlank { it.booking.uhrzeitAnmeldung } }
            .thenBy { it.currentTargetLabel }
            .thenBy { it.booking.ablaufPrio }
    )
}

internal fun All.withDriverPreparation(preparation: DriverPreparationResult): All =
    if (auftragsart == SLOT_BEREICH_WE) {
        copy(
            lkwkennzeichen = preparation.lkwKennzeichen.ifBlank { lkwkennzeichen },
            aufliegerkennzeichen = preparation.aufliegerKennzeichen.ifBlank { aufliegerkennzeichen }
        )
    } else {
        copy(
            lkwkennzeichen = preparation.lkwKennzeichen.ifBlank { lkwkennzeichen },
            aufliegerkennzeichen = preparation.aufliegerKennzeichen.ifBlank { aufliegerkennzeichen },
            leerpalettenanzahl = if (preparation.leergutDabei) preparation.leerpaletten else leerpalettenanzahl,
            driverVorgeladen = preparation.vorgeladen,
            driverVorgeladenPaletten = preparation.vorgeladenPaletten,
            driverLeergutDabei = preparation.leergutDabei
        )
    }

@Composable
private fun DriverEndToEndBlockedPopup(
    sourceAvailable: Boolean,
    onClose: () -> Unit,
    onOpenSource: () -> Unit
) {
    PopupEngineCard(
        windowKey = "driver-end-to-end-blocked",
        widthPx = 520,
        heightPx = 300,
        minWidthPx = 460,
        minHeightPx = 260,
        initialX = 420.0,
        initialY = 160.0,
        resizable = false,
        onClose = onClose
    ) {
        Div({
            style {
                width(100.percent)
                height(100.percent)
                padding(24.px)
                boxSizing("border-box")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                gap(20.px)
                color(Color.white)
            }
        }) {
            Span({
                style {
                    fontSize(18.px)
                    fontWeight("700")
                    textAlign("center")
                    lineHeight("1.35")
                }
            }) {
                Text(LocalizationManager.get("driver_e2e_pickup_first"))
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(12.px)
                    flexWrap(FlexWrap.Wrap)
                    justifyContent(JustifyContent.Center)
                }
            }) {
                LumaticaButton(
                    label = LocalizationManager.get("verstanden"),
                    onClick = onClose,
                    variant = LumaticaButtonVariant.Standard
                )
                if (sourceAvailable) {
                    LumaticaButton(
                        label = LocalizationManager.get("driver_e2e_go_to_source"),
                        onClick = onOpenSource,
                        variant = LumaticaButtonVariant.Action
                    )
                }
            }
        }
    }
}

private fun DriverBookingCardModel.currentWaStandort(): All? =
    sammelAuftraege.ifEmpty { standortRows }
        .firstOrNull { it.zustand < ZustandManager.ABGESCHLOSSEN }
        ?: sammelAuftraege.ifEmpty { standortRows }.firstOrNull()

private fun bookingMatchesDriver(
    booking: All,
    currentUserId: String,
    currentUserName: String
): Boolean {
    if (booking.slotDriverUserId == currentUserId) return true

    val normalizedUserName = currentUserName.trim().lowercase()
    if (normalizedUserName.isNotBlank() && booking.fahrername.trim().lowercase() == normalizedUserName) return true
    return false
}

private fun All.toDriverWeCardModel(
    providerFirmenId: String,
    standorteById: Map<String, All>,
    groupedAuftraege: List<All> = listOf(this),
    palettentauschAktiv: Boolean = true
): DriverBookingCardModel {
    val standortId = slotStandortId.ifBlank { standort }
    val standort = standorteById[standortId.trim()]
    val sortedGroup = groupedAuftraege.ifEmpty { listOf(this) }
    val refs = sortedGroup.map { it.lieferscheinnummer }.filter { it.isNotBlank() }.distinct()
    val primaryRef = if (refs.size > 1) refs.joinToString(", ") else lieferscheinnummer
    val totalPaletten = sortedGroup.sumOf { it.palettenanzahl.takeIf { value -> value > 0 } ?: 0 }
    val gebuchtVon = listOf(
        anbieterVorname.trim(),
        anbieterNachname.trim()
    ).filter { it.isNotBlank() }.joinToString(" ")
    val standortAdresse = listOf(
        standort?.strasse.orEmpty(),
        standort?.hausnummer.orEmpty(),
        standort?.plz.orEmpty(),
        standort?.ort.orEmpty()
    ).filter { it.isNotBlank() }.joinToString(" ")

    return DriverBookingCardModel(
        booking = this,
        providerFirmenId = providerFirmenId,
        artCode = SLOT_BEREICH_WE,
        adresseLabel = standortAdresse,
        gebuchtVonLabel = gebuchtVon,
        art = if (sortedGroup.size > 1) LocalizationManager.get("driver_group_delivery") else LocalizationManager.get("liefern"),
        primaryRef = primaryRef,
        palettenLabel = totalPaletten.takeIf { it > 0 }?.toString() ?: "0",
        lkwLabel = lkwkennzeichen,
        aufliegerLabel = aufliegerkennzeichen,
        zustandLabel = driverAuftragZustandLabel(zustand),
        firmaLabel = standort?.firma.orEmpty()
            .ifBlank { standort?.standort.orEmpty() }
            .ifBlank { firma },
        currentTargetLabel = standort?.standort.orEmpty().ifBlank { standortId }
            .ifBlank { id },
        palettentauschAktiv = palettentauschAktiv,
        sammelPin = driverWeSammelId(),
        sammelAuftraege = sortedGroup
    )
}

private fun All.driverWeSammelId(): String =
    weSammelId
        .ifBlank {
            listOf(
                datum.ifBlank { verladedatum },
                uhrzeitVon.ifBlank { verladeuhrzeit }.ifBlank { uhrzeitAnmeldung },
                slotIndex,
                slotStandortId.ifBlank { standort }
            ).joinToString("|")
        }

private fun All.driverWeSammelKey(): String {
    val stored = weSammelId.trim()
    return if (stored.isNotBlank()) {
        "SAMMEL:$stored"
    } else {
        val derived = driverWeSammelId().trim()
        if (derived.replace("|", "").isNotBlank()) "SAMMEL:$derived"
        else "SINGLE:${firmenId}:${lieferscheinnummer}:${id.ifBlank { lfdNr.toString() }}"
    }
}

private fun All.toDriverWaCardModel(
    providerFirmenId: String,
    standorteById: Map<String, All>,
    groupedAuftraege: List<All> = listOf(this),
    palettentauschAktiv: Boolean = true
): DriverBookingCardModel {
    val standort = standorteById[slotStandortId.ifBlank { standort }.trim()]
    val currentAdresse = listOf(
        strasse.ifBlank { standort?.strasse.orEmpty() },
        hausnummer.ifBlank { standort?.hausnummer.orEmpty() },
        plz.ifBlank { standort?.plz.orEmpty() },
        ort.ifBlank { standort?.ort.orEmpty() }
    ).filter { it.isNotBlank() }.joinToString(" ")
    val gebuchtVon = listOf(
        anbieterVorname.trim(),
        anbieterNachname.trim()
    ).filter { it.isNotBlank() }.joinToString(" ")
        .ifBlank { anbieterName.trim() }
        .ifBlank { firma }

    val sortedGroup = groupedAuftraege.ifEmpty { listOf(this) }
    val sortedGroupWithAdresse = sortedGroup.map { entry ->
        val entryStandort = standorteById[entry.slotStandortId.ifBlank { entry.standort }.trim()]
        entry.copy(
            strasse = entry.strasse.ifBlank { entryStandort?.strasse.orEmpty() },
            hausnummer = entry.hausnummer.ifBlank { entryStandort?.hausnummer.orEmpty() },
            plz = entry.plz.ifBlank { entryStandort?.plz.orEmpty() },
            ort = entry.ort.ifBlank { entryStandort?.ort.orEmpty() },
            firma = entry.firma.ifBlank { entryStandort?.firma.orEmpty() },
            standort = entry.standort.ifBlank { entryStandort?.standort.orEmpty() }
        )
    }
    val sammelPin = driverWaSammelPin()
    val explicitSammelPin = driverWaExplicitSammelPin()
    val totalPaletten = sortedGroup.sumOf { it.palettenanzahl.takeIf { value -> value > 0 } ?: 0 }
    val refs = sortedGroup.map { it.lieferscheinnummer }.filter { it.isNotBlank() }.distinct()
    val isMehrereLadestellen = sortedGroup.size > 1 && refs.size <= 1 && explicitSammelPin.isBlank()

    return DriverBookingCardModel(
        booking = this,
        providerFirmenId = providerFirmenId,
        artCode = SLOT_BEREICH_WA,
        adresseLabel = currentAdresse,
        gebuchtVonLabel = gebuchtVon,
        art = if (sortedGroup.size > 1 && !isMehrereLadestellen) LocalizationManager.get("driver_group_pickup") else LocalizationManager.get("driver_pick_up"),
        primaryRef = if (refs.size > 1) refs.joinToString(", ") else lieferscheinnummer,
        palettenLabel = totalPaletten.takeIf { it > 0 }?.toString().orEmpty(),
        lkwLabel = lkwkennzeichen,
        aufliegerLabel = aufliegerkennzeichen,
        zustandLabel = driverAuftragZustandLabel(zustand),
        firmaLabel = standort?.firma.orEmpty()
            .ifBlank { standort?.standort.orEmpty() }
            .ifBlank { firma },
        standortRows = sortedGroupWithAdresse,
        currentTargetLabel = driverStandortLabel(standorteById),
        palettentauschAktiv = palettentauschAktiv,
        sammelPin = sammelPin,
        sammelAuftraege = if (isMehrereLadestellen) emptyList() else sortedGroupWithAdresse
    )
}

private fun All.driverWaSammelPin(): String =
    driverWaExplicitSammelPin()
        .ifBlank { pin }
        .ifBlank { yardPinCode(this) }

private fun All.driverWaExplicitSammelPin(): String =
    waSammelPin
        .ifBlank { waSammelId }

private fun All.driverWaSammelKey(): String {
    val finalSegment = if (zustand >= ZustandManager.ABGESCHLOSSEN || zustand == ZustandManager.STORNIERT) {
        ":LS:${lieferscheinnummer.trim()}"
    } else {
        ""
    }
    val slotMultiKey = driverWaSlotMultiKey()
    if (slotMultiKey.isNotBlank()) return "SLOT_MULTI:$slotMultiKey:STANDORT:${driverWaStandortSegmentKey()}$finalSegment"

    val stored = waSammelPin
        .ifBlank { waSammelId }
        .ifBlank { pin }
        .trim()
    if (stored.isNotBlank()) return "SAMMEL:$stored$finalSegment"
    val physicalLkwKey = listOf(firmenId, lieferscheinnummer, lkwkennzeichen, aufliegerkennzeichen, fahrername)
        .map { it.trim() }
        .joinToString("|")
    return if (lieferscheinnummer.isNotBlank() && (lkwkennzeichen.isNotBlank() || fahrername.isNotBlank())) {
        "WA_LADESTELLEN:$physicalLkwKey"
    } else {
        "SINGLE:${firmenId}:${lieferscheinnummer}:${id.ifBlank { lfdNr.toString() }}"
    }
}

private fun All.driverWaStandortSegmentKey(): String =
    waStandortId
        .ifBlank { slotStandortId }
        .ifBlank { platzcode }
        .ifBlank { platz }
        .ifBlank { standort }
        .ifBlank { tor?.toString().orEmpty() }
        .ifBlank { ablaufPrio.takeIf { it > 0 }?.toString().orEmpty() }
        .ifBlank { id.ifBlank { lfdNr.toString() } }

private fun All.driverWaSlotMultiKey(): String {
    val refs = slotLadenTouren(this)
        .map { it.lieferscheinnummer.trim() }
        .filter { it.isNotBlank() }
        .distinct()
        .sorted()
    if (refs.size <= 1) return ""

    return listOf(
        firmenId,
        datum.ifBlank { verladedatum },
        uhrzeitVon.ifBlank { verladeuhrzeit }.ifBlank { uhrzeitAnmeldung },
        slotIndex,
        lkwkennzeichen,
        aufliegerkennzeichen,
        fahrername,
        refs.joinToString("+")
    ).map { it.trim() }.joinToString("|")
}

private fun All.driverStandortLabel(standorteById: Map<String, All>): String {
    val standortId = slotStandortId.ifBlank { standort }.trim()
    val source = standorteById[standortId]
    return source?.standort.orEmpty()
        .ifBlank { source?.firma.orEmpty() }
        .ifBlank { standort.takeIf { it != standortId }.orEmpty() }
        .ifBlank { standortId }
        .ifBlank { id }
}
